import { EXECUTION_TIMEOUT_SECONDS } from "../execution-limits";
import { LitElement, html, type TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import { api, csrfFetch, type JsonObject } from "../api/client";
import { i18n, type TranslationKey } from "../i18n";
import { rawJson } from "../view-model";
import "../components/product-taskbar";

interface DocRoute extends JsonObject {
  method: string;
  path: string;
  auth: string;
  summary: string;
  summary_zh?: string;
  query?: JsonObject;
  request?: JsonObject;
  errors?: string[];
  tables?: string[];
  examples?: { request?: JsonObject; query?: JsonObject; response?: unknown };
  request_required?: string[];
  array_items?: JsonObject;
  authentication_notes?: string;
  response_notes?: string;
  request_schema?: JsonObject;
  response_schema?: JsonObject;
  error_examples?: unknown;
  multipart_example?: unknown;
  response_headers?: JsonObject;
}

interface ApiDocsContract {
  version?: string;
  conventions: JsonObject;
  schemas?: JsonObject;
  error_descriptions?: Record<
    string,
    { description: string; description_zh: string }
  >;
  routes: DocRoute[];
  examples: JsonObject;
}

type ExampleLanguage = "curl" | "python" | "javascript";

const exampleLanguages: ExampleLanguage[] = ["curl", "python", "javascript"];

const exampleLabelKeys: Record<ExampleLanguage, TranslationKey> = {
  curl: "docsCurl",
  python: "docsPython",
  javascript: "docsJavaScript",
};

const groupKeys: Record<string, TranslationKey> = {
  auth: "docsGroupAuth",
  users: "docsGroupUsers",
  keys: "docsGroupKeys",
  knowledge: "docsGroupKnowledge",
  rag: "docsGroupRag",
  tasks: "docsGroupTasks",
  runs: "docsGroupRuns",
  logs: "docsGroupLogs",
  codex: "docsGroupCodex",
  docs: "docsGroupDocs",
  general: "docsGroupGeneral",
};

const routeSummaryKeys: Record<string, TranslationKey> = {
  "POST /api/auth/login": "docsRouteAuthLogin",
  "POST /api/auth/logout": "docsRouteAuthLogout",
  "GET /api/auth/me": "docsRouteAuthMe",
  "GET /api/users": "docsRouteUsersList",
  "POST /api/users": "docsRouteUsersCreate",
  "PATCH /api/users/{id}": "docsRouteUsersUpdate",
  "DELETE /api/users/{id}": "docsRouteUsersDelete",
  "GET /api/keys": "docsRouteKeysList",
  "POST /api/keys": "docsRouteKeysCreate",
  "DELETE /api/keys/{id}": "docsRouteKeysDelete",
  "GET /api/rag/search": "docsRouteRagSearch",
  "GET /api/tasks": "docsRouteTasksList",
  "POST /api/tasks": "docsRouteTasksCreate",
  "PATCH /api/tasks/{id}": "docsRouteTasksUpdate",
  "DELETE /api/tasks/{id}": "docsRouteTasksDelete",
  "POST /api/tasks/{id}/run": "docsRouteTasksRun",
  "GET /api/tasks/{id}/runs": "docsRouteTaskRuns",
  "GET /api/runs/{run_id}": "docsRouteRunRead",
  "GET /api/logs": "docsRouteLogsRead",
  "POST /api/codex/execute": "docsRouteCodexExecute",
  "GET /api/codex/runs": "docsRouteCodexRuns",
  "GET /api/codex/runs/{id}/events": "docsRouteCodexRunEvents",
  "GET /api/docs": "docsRouteDocsRead",
};

const knowledgeGroups = new Set([
  "catalog",
  "search",
  "repositories",
  "components",
  "files",
  "documents",
  "locations",
  "chunks",
  "symbols",
  "contracts",
  "evidence",
  "cases",
  "resources:batchGet",
  "ingestion-sources",
  "ingestions",
  "maintenance",
]);

export function routeGroup(route: DocRoute): string {
  if (route.path === "/api/models") return "codex";
  const group = route.path.split("/")[2] || "general";
  return knowledgeGroups.has(group) ? "knowledge" : group;
}

function historyQuery(route: DocRoute): JsonObject | undefined {
  if (route.method !== "GET") return route.examples?.query;
  if (route.path === "/api/codex/runs") {
    return {
      page: 1,
      page_size: 10,
      from: "2026-01-01T00:00:00Z",
      to: "2026-01-02T00:00:00Z",
      ...route.examples?.query,
    };
  }
  if (route.path === "/api/codex/runs/{id}/events") {
    return { page: 1, page_size: 100, ...route.examples?.query };
  }
  return route.examples?.query;
}

export function concretePath(route: DocRoute): string {
  let path = route.path
    .replace("{run_id}", "1")
    .replace("{repository_key}", "cangjie_runtime")
    .replace("{component_key}", "runtime")
    .replace("{contract_id}", "ct:example")
    .replace("{behavior_id}", "bh:example")
    .replace("{relation_id}", "rel:example")
    .replace(
      "{id}",
      route.path === "/api/codex/runs/{id}/events"
        ? "example-run"
        : route.path.startsWith("/api/documents")
          ? "markdown:example"
          : route.path.startsWith("/api/files")
            ? "file:example"
            : "ct:example",
    );

  const exampleQuery = historyQuery(route);
  if (exampleQuery) {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(exampleQuery))
      query.set(key, String(value));
    return `${path}?${query}`;
  }
  if (route.path === "/api/search") {
    path += "?q=compatibility&types=contract,symbol&page_size=10";
  }
  if (route.path === "/api/rag/search") {
    path += "?q=breaking%20change&mode=auto&top_k=5";
  }
  return path;
}

export function sampleBody(route: DocRoute): JsonObject | null {
  if (route.examples?.request) return route.examples.request;
  const key = `${route.method} ${route.path}`;
  const samples: Record<string, JsonObject> = {
    "POST /api/auth/login": {
      username: "user@example.com",
      password: "secure-password",
    },
    "POST /api/users": {
      username: "user@example.com",
      password: "secure-password",
      role: "viewer",
      is_active: true,
    },
    "POST /api/keys": {
      name: "mcp-client",
      scopes: ["knowledge:curate", "rag:read"],
    },
    "POST /api/tasks": {
      name: "nightly-index",
      category: "script",
      shell: "python",
      script: 'print("maintenance-check")',
      start_at: null,
      interval_seconds: 3600,
      timeout_seconds: EXECUTION_TIMEOUT_SECONDS,
      enabled: true,
      env: {},
    },
    "POST /api/codex/execute": {
      apikey: "provider-secret",
      base_url: "https://api.openai.com/v1",
      system_prompt: "Answer with compatibility evidence.",
      prompt: "Summarize the relevant compatibility changes.",
      skills: [],
      model: "gpt-5.5",
      reasoning_effort: "medium",
      execution_mode: "unrestricted",
      envs: {},
    },
    "POST /api/repositories": {
      repository_key: "example_repository",
      name: "Example Repository",
      url: "https://example.com/repository.git",
    },
    "POST /api/components": {
      repository: "cangjie_runtime",
      component_key: "runtime",
      name: "Runtime",
    },
    "POST /api/files": {
      namespace: "repository:cangjie_runtime",
      relative_path: "src/example.cj",
      language: "cangjie",
      file_kind: "source",
    },
    "POST /api/locations": {
      file_id: "file:example",
      line_start: 10,
      line_end: 20,
    },
    "POST /api/chunks": {
      location_id: "loc:example",
      kind: "source",
      language: "cangjie",
      text: "public func example() {}",
    },
    "POST /api/symbols": {
      repository: "cangjie_runtime",
      component: "runtime",
      location_id: "loc:example",
      kind: "function",
      qualified_name: "example.run",
    },
    "POST /api/evidence": {
      location_id: "loc:example",
      level: "source",
      text: "Compatibility evidence text.",
    },
    "POST /api/contracts": {
      kind: "api",
      family: "declaration",
      component: "runtime",
      name: "Example API",
      detail: { public_commitment: true },
      evidence: [{ id: "ev:example", relation: "supports" }],
    },
    "POST /api/contracts/{contract_id}/behaviors": {
      kind: "observable_change",
      old_observable: "old behavior",
      new_observable: "new behavior",
      compatibility_sensitive: true,
    },
    "POST /api/contracts/{contract_id}/consumers": {
      component: "runtime",
      symbol_name: "example.consumer",
      relation: "calls",
      observations: [],
    },
    "POST /api/cases": {
      decision: "incompatible",
      review_status: "open",
      repositories: ["cangjie_runtime"],
      contracts: ["ct:example"],
      evidence: ["ev:example"],
      rule_hits: ["api-stability"],
    },
    "POST /api/resources:batchGet": {
      references: ["ct:example", "sym:example", "ev:example"],
    },
  };

  if (samples[key]) return samples[key];
  if (["POST", "PATCH"].includes(route.method)) return {};
  return null;
}

function schemaSample(schema: unknown): unknown {
  if (typeof schema === "string") {
    if (/boolean/.test(schema)) return false;
    if (/integer|number/.test(schema)) return 1;
    return "<replace>";
  }
  if (!schema || typeof schema !== "object") return "<replace>";
  const value = schema as JsonObject;
  if (value.example !== undefined) return value.example;
  if (value.default !== undefined) return value.default;
  if (Array.isArray(value.enum)) return value.enum[0];
  if (value.type === "array") return [schemaSample(value.items)];
  if (value.type === "boolean") return false;
  if (["integer", "number"].includes(String(value.type))) return 1;
  if (value.properties)
    return Object.fromEntries(
      Object.entries(value.properties as JsonObject).map(([key, field]) => [
        key,
        schemaSample(field),
      ]),
    );
  return "<replace>";
}
export function debuggerPreset(route: DocRoute) {
  const safe = ["GET", "HEAD"].includes(route.method);
  const multipart = !safe && !!route.multipart_example;
  const query = new URLSearchParams();
  const fields =
    historyQuery(route) ||
    Object.fromEntries(
      Object.keys(route.query || {}).map((key) => [key, `{{QUERY_${key}}}`]),
    );
  for (const [key, value] of Object.entries(fields))
    query.set(key, String(value));
  let sample: unknown = safe ? null : sampleBody(route);
  if (!safe && (!sample || !Object.keys(sample as object).length)) {
    sample = route.request_schema
      ? schemaSample(route.request_schema)
      : Object.fromEntries(
          Object.entries(route.request || {}).map(([key, value]) => [
            key,
            schemaSample(value),
          ]),
        );
  }
  if (multipart) {
    sample = {
      ...(((route.multipart_example as JsonObject).fields as JsonObject) || {}),
    };
    delete (sample as JsonObject).file;
  }
  const headers: Record<string, string> = { Accept: "application/json" };
  if (!safe && !multipart && sample && Object.keys(sample as object).length)
    headers["Content-Type"] = "application/json";
  return {
    method: route.method,
    path: route.path + (query.size ? `?${query}` : ""),
    headers: rawJson(headers),
    body: safe ? "" : rawJson(sample || {}),
    multipart,
  };
}

function requiresApiKey(route: DocRoute): boolean {
  return /(?:^|\s)api-key:/.test(route.auth);
}

export function isExternalRoute(route: DocRoute): boolean {
  return (
    routeGroup(route) === "knowledge" ||
    route.path === "/api/rag/search" ||
    route.path === "/api/codex/execute" ||
    (route.method === "GET" &&
      [
        "/api/models",
        "/api/codex/runs",
        "/api/codex/runs/{id}/events",
      ].includes(route.path))
  );
}

function routeKey(route: DocRoute): string {
  return `${route.method} ${route.path}`;
}

function exampleHeaders(
  route: DocRoute,
  hasBody: boolean,
): Record<string, string> {
  const headers: Record<string, string> = {};
  if (hasBody) headers["Content-Type"] = "application/json";
  if (requiresApiKey(route)) headers["X-API-Key"] = "cpl_...";
  return headers;
}

export function generateExamples(route: DocRoute): Record<string, string> {
  const path = concretePath(route);
  const url = `https://cpl.cangjie.design${path}`;
  const body = sampleBody(route);
  const headers = exampleHeaders(route, body !== null);
  const curlHeaders = Object.entries(headers)
    .map(([name, value]) => ` -H '${name}: ${value}'`)
    .join("");
  const curlBody = body ? ` -d '${JSON.stringify(body)}'` : "";
  const fetchOptions: JsonObject = {
    method: route.method,
    ...(Object.keys(headers).length ? { headers } : {}),
    ...(body ? { body: "JSON.stringify(payload)" } : {}),
  };
  const javascriptOptions = rawJson(fetchOptions).replace(
    '"JSON.stringify(payload)"',
    "JSON.stringify(payload)",
  );

  // The examples are generated from the live method, path, auth, and body schema.
  return {
    curl: `curl -X ${route.method}${curlHeaders}${curlBody} '${url}'`,
    python: [
      "import requests",
      ...(body ? ["import json"] : []),
      "",
      ...(body
        ? [`payload = json.loads(${JSON.stringify(JSON.stringify(body))})`, ""]
        : []),
      `response = requests.request(`,
      `    "${route.method}",`,
      `    "${url}",`,
      ...(Object.keys(headers).length
        ? [`    headers=${JSON.stringify(headers)},`]
        : []),
      ...(body ? ["    json=payload,"] : []),
      ")",
      "response.raise_for_status()",
      "print(response.json())",
    ].join("\n"),
    javascript: [
      ...(body ? [`const payload = ${rawJson(body)};`, ""] : []),
      `const response = await fetch("${url}", ${javascriptOptions});`,
      "const data = await response.json();",
      "if (!response.ok) throw new Error(data.error?.message ?? response.statusText);",
      "console.log(data);",
    ].join("\n"),
  };
}

@customElement("api-explorer")
export class ApiExplorer extends LitElement {
  @state() private contract: ApiDocsContract | null = null;
  @state() private selectedGroup = "knowledge";
  @state() private selectedRouteKey = "";
  @state() private debugMethod = "GET";
  @state() private debugPath = "";
  @state() private debugHeaders = "{}";
  @state() private debugBody = "";
  @state() private debugKey = "";
  @state() private debugMultipart = false;
  private debugVariables: string[] = [];
  @state() private output: string | null = null;
  @state() private loadError = "";
  @state() private busy = false;
  @state() private selectedExample: ExampleLanguage = "curl";

  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("languagechange", this.languageChanged);
    void this.loadContract();
  }

  disconnectedCallback() {
    window.removeEventListener("languagechange", this.languageChanged);
    super.disconnectedCallback();
  }

  private languageChanged = () => this.requestUpdate();

  private async loadContract() {
    try {
      const contract = (await api.docs()) as unknown as ApiDocsContract;
      const routes = contract.routes.filter(isExternalRoute);
      this.contract = { ...contract, routes };

      const firstKnowledgeRoute = routes.find(
        (route) => routeGroup(route) === "knowledge",
      );
      const initialRoute = firstKnowledgeRoute || routes[0];
      if (initialRoute) {
        this.selectedGroup = routeGroup(initialRoute);
        this.selectedRouteKey = routeKey(initialRoute);
        await this.updateComplete;
        this.configureDebugger(initialRoute, false);
      }
    } catch (error) {
      this.loadError = (error as Error).message;
    }
  }

  private async send(event: SubmitEvent) {
    event.preventDefault();
    this.busy = true;
    const values = Object.fromEntries(
      new FormData(event.currentTarget as HTMLFormElement),
    );
    const method = String(values.method || "GET").toUpperCase();

    try {
      const parsedHeaders: unknown = JSON.parse(String(values.headers || "{}"));
      if (
        typeof parsedHeaders !== "object" ||
        parsedHeaders === null ||
        Array.isArray(parsedHeaders)
      ) {
        throw new Error(i18n.t("docsInvalidHeaders"));
      }
      const headers = new Headers(parsedHeaders as Record<string, string>);
      if (headers.has("X-API-Key"))
        throw new Error(i18n.t("docsKeySingleSource"));
      headers.set("Accept", "application/json");
      if (values.key) headers.set("X-API-Key", String(values.key));
      const path = String(values.path || "");
      if (
        this.debugVariables.some((variable) =>
          decodeURIComponent(path).includes(variable),
        ) ||
        headers.get("X-API-Key") === "<API_KEY>" ||
        String(values.key || "") === "<API_KEY>"
      )
        throw new Error(i18n.t("docsReplacePlaceholders"));
      if (!path.startsWith("/api/") || path.startsWith("//"))
        throw new Error(i18n.t("docsLocalPath"));

      const bodyText = String(values.body || "").trim();
      let body: string | FormData | undefined;
      if (
        !["GET", "HEAD"].includes(method) &&
        (bodyText || this.debugMultipart)
      ) {
        if (
          this.debugMultipart &&
          (!(values.file instanceof File) || !values.file.name)
        )
          throw new Error(i18n.t("docsChooseFile"));
        try {
          const parsed = JSON.parse(bodyText || "{}");
          const hasPlaceholder = (value: unknown): boolean =>
            value === "<replace>" ||
            (!!value &&
              typeof value === "object" &&
              Object.values(value).some(hasPlaceholder));
          if (hasPlaceholder(parsed)) throw new Error("placeholder");
          if (this.debugMultipart) {
            const file = values.file;
            if (!(file instanceof File) || !file.name) throw new Error("file");
            const form = new FormData();
            for (const [key, value] of Object.entries(parsed))
              form.set(
                key,
                typeof value === "string" ? value : JSON.stringify(value),
              );
            form.set("file", file);
            body = form;
          } else body = JSON.stringify(parsed);
        } catch {
          throw new Error(i18n.t("docsInvalidBody"));
        }
        if (this.debugMultipart) headers.delete("Content-Type");
        else headers.set("Content-Type", "application/json");
      }

      // Keep non-2xx responses intact so the debugger can display error contracts.
      const response = await csrfFetch(String(values.path), {
        method,
        headers,
        body,
        credentials: "same-origin",
      });
      const responseText = await response.text();
      let responseBody: unknown = responseText;
      try {
        responseBody = responseText ? JSON.parse(responseText) : null;
      } catch {
        // Plain text is a valid diagnostic response and remains visible as-is.
      }
      this.output = rawJson({
        status: response.status,
        status_text: response.statusText,
        ok: response.ok,
        body: responseBody,
      });
    } catch (error) {
      this.output = rawJson({
        ok: false,
        error: {
          message: (error as Error).message,
        },
      });
    } finally {
      this.busy = false;
    }
  }

  private configureDebugger(route: DocRoute, scroll = true) {
    const preset = debuggerPreset(route);
    this.debugMethod = preset.method;
    this.debugPath = preset.path;
    this.debugVariables = [
      ...(route.path.match(/\{[^{}]+\}/g) || []),
      ...(decodeURIComponent(preset.path).match(/\{\{QUERY_[^{}]+\}\}/g) || []),
    ];
    this.debugHeaders = preset.headers;
    this.debugBody = preset.body;
    this.debugMultipart = preset.multipart;
    this.output = null;
    if (scroll) {
      this.querySelector(".docs-main")?.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }

  private async selectGroup(group: string) {
    const route = this.contract?.routes.find(
      (candidate) => routeGroup(candidate) === group,
    );
    if (!route) return;
    this.selectedGroup = group;
    this.selectedRouteKey = routeKey(route);
    await this.updateComplete;
    this.configureDebugger(route);
  }

  private async selectRoute(route: DocRoute) {
    this.selectedGroup = routeGroup(route);
    this.selectedRouteKey = routeKey(route);
    await this.updateComplete;
    this.configureDebugger(route);
  }

  private groupLabel(group: string): string {
    return i18n.t(groupKeys[group] || "docsGroupGeneral");
  }

  private summary(route: DocRoute): string {
    const key = routeSummaryKeys[`${route.method} ${route.path}`];
    if (key) return i18n.t(key);
    return i18n.language === "zh-CN"
      ? route.summary_zh || route.summary
      : route.summary;
  }

  private authLabel(auth: string): string {
    if (auth.includes("session") && auth.includes("api-key:")) return auth;
    if (auth.startsWith("session")) return i18n.t("docsSession");
    if (!auth.startsWith("api-key:")) return i18n.t("docsAuthNotRequired");
    const scope = auth.split(":").slice(1).join(":");
    return i18n.t("docsAuthRequired", { scope });
  }

  private selectExample(language: ExampleLanguage) {
    this.selectedExample = language;
  }

  private async handleExampleKeydown(event: KeyboardEvent) {
    const currentIndex = exampleLanguages.indexOf(this.selectedExample);
    let nextIndex: number | null = null;

    if (event.key === "ArrowRight") {
      nextIndex = (currentIndex + 1) % exampleLanguages.length;
    } else if (event.key === "ArrowLeft") {
      nextIndex =
        (currentIndex - 1 + exampleLanguages.length) % exampleLanguages.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = exampleLanguages.length - 1;
    }

    if (nextIndex === null) return;
    event.preventDefault();
    this.selectedExample = exampleLanguages[nextIndex];
    await this.updateComplete;
    this.querySelector<HTMLElement>(
      `#docs-example-tab-${this.selectedExample}`,
    )?.focus();
  }

  private schemaRows(value: JsonObject): TemplateResult {
    return html`
      <dl>
        ${Object.entries(value).map(
          ([name, definition]) => html`
            <dt><code>${name}</code></dt>
            <dd>
              <code>${this.schemaDescription(definition)}</code>
            </dd>
          `,
        )}
      </dl>
    `;
  }

  private schemaDescription(definition: unknown): string {
    if (typeof definition === "string") return definition;
    if (
      typeof definition === "object" &&
      definition !== null &&
      "description" in definition
    ) {
      const value = definition as {
        description?: string;
        description_zh?: string;
      };
      return i18n.language === "zh-CN"
        ? value.description_zh || value.description || ""
        : value.description || value.description_zh || "";
    }
    return rawJson(definition);
  }

  private errorDescription(code: string): string {
    const value = this.contract?.error_descriptions?.[code];
    if (!value) return code;
    return i18n.language === "zh-CN"
      ? value.description_zh || value.description
      : value.description || value.description_zh;
  }

  private responseEnvelope(route: DocRoute): unknown {
    if (route.examples && "response" in route.examples)
      return route.examples.response;
    const conventions = this.contract?.conventions || {};
    return route.method === "GET" && route.query && "page" in route.query
      ? conventions.list_envelope
      : conventions.success_envelope;
  }

  private renderDebugger() {
    return html`
      <section class="docs-debug-section">
        <h3>${i18n.t("docsDebugger")}</h3>
        <form class="debugger" @submit=${this.send}>
          <label>
            ${i18n.t("docsMethod")}
            <select
              class="a2k-native-input"
              name="method"
              .value=${this.debugMethod}
              @change=${(event: Event) => {
                this.debugMethod = (event.target as HTMLSelectElement).value;
              }}
            >
              <option>GET</option>
              <option>POST</option>
              <option>PATCH</option>
              <option>DELETE</option>
            </select>
          </label>
          <label
            >${i18n.t("docsPath")}
            <input
              class="a2k-native-input"
              name="path"
              required
              .value=${this.debugPath}
              @input=${(event: Event) => {
                this.debugPath = (event.target as HTMLInputElement).value;
              }}
            />
          </label>
          <p>${i18n.t("docsPrefillHelp")}</p>
          <label>
            ${i18n.t("docsApiKey")}
            <input
              class="a2k-native-input"
              name="key"
              type="password"
              autocomplete="off"
              placeholder=${i18n.t("docsKeyPlaceholder")}
              .value=${this.debugKey}
              @input=${(event: Event) => {
                this.debugKey = (event.target as HTMLInputElement).value;
              }}
            />
          </label>
          <label>
            ${i18n.t("docsHeaders")}
            <textarea
              name="headers"
              rows="4"
              .value=${this.debugHeaders}
              @input=${(event: Event) => {
                this.debugHeaders = (event.target as HTMLTextAreaElement).value;
              }}
            ></textarea>
          </label>
          <label>
            ${i18n.t("docsBody")}
            <textarea
              name="body"
              rows="8"
              .value=${this.debugBody}
              placeholder=${i18n.t("docsBodyPlaceholder")}
              ?disabled=${["GET", "HEAD"].includes(this.debugMethod)}
              @input=${(event: Event) => {
                this.debugBody = (event.target as HTMLTextAreaElement).value;
              }}
            ></textarea>
          </label>
          ${
            this.debugMultipart
              ? html`<label>
                  ${i18n.t("docsUploadFile")}
                  <input type="file" name="file" required />
                </label>`
              : null
          }
          <a2k-button type="submit" ?disabled=${this.busy}>
            ${this.busy ? i18n.t("working") : i18n.t("docsSend")}
          </a2k-button>
        </form>
      </section>
      <section class="docs-debug-section">
        <h3>${i18n.t("docsResponse")}</h3>
        <pre class="code-output">
${this.output || i18n.t("docsResponsePending")}</pre>
      </section>
    `;
  }

  private renderEndpoint(route: DocRoute) {
    const examples = generateExamples(route);
    return html`
      <section class="docs-content-panel">
        <h2>${route.method} ${route.path}</h2>
        <a2k-panel class="endpoint docs-detail">
          <div class="endpoint-title">
            <span class="method method-${route.method.toLowerCase()}">
              ${route.method}
            </span>
            <code>${route.path}</code>
          </div>
          <section class="docs-contract-section">
            <h3>${i18n.t("docsEndpointPurpose")}</h3>
            <p>${this.summary(route)}</p>
          </section>
          <section class="docs-contract-section">
            <h3>${i18n.t("docsAuthentication")}</h3>
            <p>${i18n.t("docsAuthDescription")}</p>
            <code>${this.authLabel(route.auth)}</code>
            ${route.authentication_notes ? html`<p>${route.authentication_notes}</p>` : null}
          </section>
          <section class="docs-contract-section">
            <h3>${i18n.t("docsQuery")}</h3>
            ${
              route.query
                ? this.schemaRows(route.query)
                : html`<p>${i18n.t("docsNoQuery")}</p>`
            }
          </section>
          <section class="docs-contract-section">
            <h3>${i18n.t("docsRequestSchema")}</h3>
            ${
              route.request_schema
                ? html`<pre class="code-output">
${rawJson(route.request_schema)}</pre>`
                : route.request
                  ? this.schemaRows(route.request)
                  : html`<p>${i18n.t("docsNoRequestBody")}</p>`
            }
          </section>
          <section class="docs-contract-section">
            <h3>${i18n.t("docsResponseSchema")}</h3>
            ${route.response_notes ? html`<p>${route.response_notes}</p>` : null}
            ${route.response_schema ? html`<pre class="code-output">${rawJson(route.response_schema)}</pre>` : null}
            ${route.response_headers ? html`<pre class="code-output">${rawJson(route.response_headers)}</pre>` : null}
            <h4>${i18n.t("docsResponseExample")}</h4>
            <pre class="code-output">
${rawJson(this.responseEnvelope(route))}</pre>
          </section>
          <section class="docs-contract-section">
            <h3>${i18n.t("docsErrors")}</h3>
            ${route.error_examples ? html`<pre class="code-output">${rawJson(route.error_examples)}</pre>` : null}
            ${
              route.errors?.length
                ? html`<ul>
                    ${route.errors.map(
                      (error) =>
                        html`<li>
                          <code>${error}</code>
                          <span>${this.errorDescription(error)}</span>
                        </li>`,
                    )}
                  </ul>`
                : html`<p>${i18n.t("docsNoErrors")}</p>`
            }
          </section>
          ${
            route.tables?.length
              ? html`
                  <section class="docs-contract-section">
                    <h3>${i18n.t("docsTables")}</h3>
                    <p>
                      ${route.tables.map((table) => html`<code>${table}</code> `)}
                    </p>
                  </section>
                `
              : null
          }
          <section class="docs-contract-section docs-examples">
            <h3>${i18n.t("docsExamples")}</h3>
            ${route.multipart_example ? html`<pre class="code-output">${rawJson(route.multipart_example)}</pre>` : null}
            <div
              class="docs-example-tabs"
              role="tablist"
              aria-label=${i18n.t("docsExamples")}
              @keydown=${this.handleExampleKeydown}
            >
              ${exampleLanguages.map(
                (language) => html`
                  <button
                    id=${`docs-example-tab-${language}`}
                    type="button"
                    role="tab"
                    aria-selected=${String(language === this.selectedExample)}
                    aria-controls="docs-example-panel"
                    tabindex=${language === this.selectedExample ? "0" : "-1"}
                    @click=${() => this.selectExample(language)}
                  >
                    ${i18n.t(exampleLabelKeys[language])}
                  </button>
                `,
              )}
            </div>
            <pre
              id="docs-example-panel"
              class="code-output docs-example-panel"
              role="tabpanel"
              tabindex="0"
              aria-labelledby=${`docs-example-tab-${this.selectedExample}`}
            >
${examples[this.selectedExample]}</pre>
          </section>
        </a2k-panel>
        ${this.renderDebugger()}
      </section>
    `;
  }

  private renderNavigation(routes: DocRoute[]) {
    const groups = [...new Set(routes.map(routeGroup))];
    const endpoints = routes.filter(
      (route) => routeGroup(route) === this.selectedGroup,
    );
    return html`
      <aside class="docs-nav">
        <nav class="docs-nav-primary" aria-label=${i18n.t("docsTitle")}>
          ${groups.map(
            (group) => html`
              <button
                type="button"
                aria-current=${String(group === this.selectedGroup)}
                @click=${() => void this.selectGroup(group)}
              >
                ${this.groupLabel(group)}
              </button>
            `,
          )}
        </nav>
        <nav class="docs-nav-endpoints" aria-label=${i18n.t("docsEndpoints")}>
          <strong>${i18n.t("docsEndpoints")}</strong>
          ${endpoints.map(
            (route) => html`
              <button
                type="button"
                aria-current=${String(
                  routeKey(route) === this.selectedRouteKey,
                )}
                @click=${() => void this.selectRoute(route)}
              >
                <span class="docs-endpoint-main">
                  <span class="method method-${route.method.toLowerCase()}">
                    ${route.method}
                  </span>
                  <code>${route.path}</code>
                </span>
                <small>${this.summary(route)}</small>
              </button>
            `,
          )}
        </nav>
      </aside>
    `;
  }

  render() {
    const routes = this.contract?.routes || [];
    const selectedRoute =
      routes.find((route) => routeKey(route) === this.selectedRouteKey) ||
      routes[0];

    return html`
      <a2k-desktop class="docs-desktop">
        <div class="docs-frame">
          <header class="docs-header">
            <div>
              <span class="kicker">${i18n.t("docsKicker")}</span>
              <h1>${i18n.t("docsTitle")}</h1>
              <p>${i18n.t("docsDescription")}</p>
            </div>
            <div>
              <a2k-button size="small" @click=${() => i18n.toggle()}>
                ${i18n.t("language")}
              </a2k-button>
              <a class="docs-back" href="/">${i18n.t("docsConsole")}</a>
            </div>
          </header>
          ${this.renderNavigation(routes)}
          <main class="docs-main">
            ${
              this.loadError
                ? html`
                    <p class="status status-error">
                      ${i18n.t("docsLoadError", { message: this.loadError })}
                    </p>
                  `
                : !this.contract
                  ? html`<a2k-progress>${i18n.t("docsLoading")}</a2k-progress>`
                  : selectedRoute
                    ? this.renderEndpoint(selectedRoute)
                    : html`<p class="empty-state">${i18n.t("empty")}</p>`
            }
          </main>
        </div>
        <cpl-taskbar active="overview"></cpl-taskbar>
      </a2k-desktop>
    `;
  }
}
